import React, { useRef, useEffect, useState } from 'react';
import { Home, BarChart3, Users, Flag, MoreHorizontal, Menu, X, User2, Mail } from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import efafcLogo from '../../assets/efafc.png';

const navItems = [
  { to: '/', label: 'ACCUEIL', icon: <Home size={20} /> },
  { to: '/stats', label: 'STATS', icon: <BarChart3 size={20} /> },
  { to: '/fanzone', label: 'FAN ZONE', icon: <Flag size={20} /> },
  { to: '/equipes', label: 'ÉQUIPES', icon: <Users size={20} /> },
  { to: '/plus', label: 'PLUS', icon: <MoreHorizontal size={20} /> },
];

const extraLinks = [
  { to: '/club', label: 'CLUB' },
  { to: '/boutique', label: 'BOUTIQUE' },
  { to: '/matchs', label: 'MATCHS' },
  { to: '/galerie', label: 'GALERIE' },
  { to: '/partenaires', label: 'PARTENAIRES' },
];

const socialLinks = [
  { href: 'https://www.facebook.com/', icon: <i className="fa-brands fa-facebook-f" /> },
  { href: 'https://www.instagram.com/', icon: <i className="fa-brands fa-instagram" /> },
  { href: 'https://twitter.com/', icon: <i className="fa-brands fa-x-twitter" /> },
  { href: 'https://www.youtube.com/', icon: <i className="fa-brands fa-youtube" /> },
  { href: 'https://www.tiktok.com/', icon: <i className="fa-brands fa-tiktok" /> },
];

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const containerRef = useRef(null);
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const linkRefs = useRef([]);
  const [plusOpen, setPlusOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const activeIdx = navItems.findIndex(item =>
      location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to))
    );
    if (linkRefs.current[activeIdx] && window.innerWidth >= 768) {
      const el = linkRefs.current[activeIdx];
      setUnderlineStyle({
        left: el.offsetLeft,
        width: el.offsetWidth
      });
    }
    
    if (plusOpen || mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [location.pathname, plusOpen, mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleProfileClick = () => {
    if (currentUser) {
      navigate('/profile');
    } else {
      navigate('/auth');
    }
    closeMobileMenu();
  };

  const handleContactClick = () => {
    navigate('/contact');
    closeMobileMenu();
  };

  return (
    <>
      {/* Desktop Navbar */}
      <div className="hidden md:block fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
        <div ref={containerRef} className="relative flex items-center justify-between bg-[#FFE400] rounded-2xl px-3 py-2 shadow-xl w-[90vw] max-w-2xl">
          {/* Barre de soulignement animée */}
          <span
            className="absolute bottom-3 h-0.5 rounded-full bg-[#001B56] transition-all duration-300"
            style={{
              left: underlineStyle.left + (underlineStyle.width / 2) - 9,
              width: 18,
              pointerEvents: 'none',
            }}
          />
          {navItems.map((item, idx) => {
            const isActive = location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to));
            if (item.to === '/plus') {
              return (
                <button
                  key={item.to}
                  ref={el => (linkRefs.current[idx] = el)}
                  className={`flex flex-col items-center px-2 py-1 relative transition group font-accent ${plusOpen ? 'text-[#001B56] font-extrabold' : 'text-[#001B56] opacity-80 hover:opacity-100'}`}
                  onClick={() => setPlusOpen(true)}
                  type="button"
                >
                  <span className={`mb-1 transition-all duration-300 ${plusOpen ? 'translate-y-[-8px]' : ''}`}>{item.icon}</span>
                  <span className={`text-xs font-bold tracking-wide transition-all duration-300 ${plusOpen ? 'translate-y-[-8px]' : ''}`}>{item.label}</span>
                </button>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                ref={el => (linkRefs.current[idx] = el)}
                className={({ isActive: navActive }) =>
                  `flex flex-col items-center px-2 py-1 relative transition group font-accent ${
                    isActive || navActive
                      ? 'text-[#001B56] font-extrabold'
                      : 'text-[#001B56] opacity-80 hover:opacity-100'
                  }`
                }
                end={item.to === '/'}
              >
                <span className={`mb-1 transition-all duration-300 ${isActive ? 'translate-y-[-8px]' : ''}`}>{item.icon}</span>
                <span className={`text-xs font-bold tracking-wide transition-all duration-300 ${isActive ? 'translate-y-[-8px]' : ''}`}>{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>

      {/* Mobile Navbar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#FFE400] shadow-lg">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Logo à gauche */}
          <NavLink to="/" className="flex items-center" onClick={closeMobileMenu}>
            <img src={efafcLogo} alt="EFAFC Logo" className="h-12 drop-shadow-md" />
          </NavLink>

          {/* Boutons d'action et menu burger */}
          <div className="flex items-center gap-2">
            {/* Bouton Contact */}
            <button
              onClick={handleContactClick}
              className="w-10 h-10 rounded-lg bg-[#001B56] flex items-center justify-center shadow-md hover:bg-[#002B76] transition text-[#FFE400]"
              aria-label="Contact"
            >
              <Mail size={20} />
            </button>
            
            {/* Bouton Profil/Auth */}
            <button
              onClick={handleProfileClick}
              className="w-10 h-10 rounded-lg bg-[#001B56] flex items-center justify-center shadow-md hover:bg-[#002B76] transition text-[#FFE400]"
              aria-label={currentUser ? "Mon profil" : "Se connecter"}
            >
              <User2 size={20} />
            </button>

            {/* Bouton Menu */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-10 h-10 rounded-lg bg-[#001B56] flex items-center justify-center shadow-md hover:bg-[#002B76] transition text-[#FFE400] ml-1"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/70 z-40 animate-fade-in"
            onClick={closeMobileMenu}
          />
          <div className="md:hidden fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-[#1A1A2E] z-50 transform transition-transform duration-300">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b border-gray-700">
                <span className="text-[#BDFF00] font-title font-bold text-lg">Menu</span>
                <button
                  onClick={closeMobileMenu}
                  className="text-[#FFE400] p-2"
                  aria-label="Fermer le menu"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                <div className="p-4 space-y-2">
                  {navItems.filter(item => item.to !== '/plus').map(item => {
                    const isActive = location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to));
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={closeMobileMenu}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition font-accent ${
                          isActive
                            ? 'bg-[#FFE400] text-[#001B56] font-bold'
                            : 'text-white hover:bg-gray-700'
                        }`}
                      >
                        {item.icon}
                        <span className="font-medium">{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>

                <div className="p-4 border-t border-gray-700">
                  <div className="mb-4">
                    <span className="text-[#BDFF00] text-sm font-accent font-bold uppercase tracking-widest">Plus sur le club</span>
                  </div>
                  <div className="space-y-2">
                    {extraLinks.map(link => (
                      <NavLink
                        key={link.to}
                        to={link.to}
                        onClick={closeMobileMenu}
                        className="block px-4 py-3 text-white hover:bg-gray-700 rounded-xl transition font-accent"
                      >
                        {link.label}
                      </NavLink>
                    ))}
                  </div>
                </div>

                <div className="p-4 border-t border-gray-700">
                  <div className="flex justify-center gap-4">
                    {socialLinks.map((s, i) => (
                      <a
                        key={i}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#FFE400] text-xl hover:text-[#BDFF00] transition"
                      >
                        {s.icon}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Desktop Plus Menu */}
      {plusOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-all duration-300"
            onClick={() => setPlusOpen(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="relative bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#1A1A2E] rounded-3xl shadow-2xl border border-[#FFE400]/20 w-full max-w-2xl transform transition-all duration-500 ease-out scale-100 opacity-100 animate-slide-up">
              {/* Header avec bouton de fermeture */}
              <div className="relative px-8 pt-6 pb-4">
                <button
                  className="absolute top-4 right-6 w-10 h-10 rounded-full bg-[#FFE400]/10 hover:bg-[#FFE400]/20 flex items-center justify-center text-[#FFE400] hover:text-[#BDFF00] transition-all duration-200 hover:scale-110 group"
                  onClick={() => setPlusOpen(false)}
                  aria-label="Fermer le menu"
                >
                  <X size={20} className="group-hover:rotate-90 transition-transform duration-200" />
                </button>
                
                <div className="text-center">
                  <h3 className="text-[#BDFF00] text-sm font-accent font-bold uppercase tracking-widest mb-2">
                    Découvrir le club
                  </h3>
                  <div className="w-16 h-0.5 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] mx-auto rounded-full"></div>
                </div>
              </div>

              {/* Contenu principal */}
              <div className="px-8 pb-6">
                {/* Navigation principale */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
                  {extraLinks.map((link, index) => (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      className="group relative bg-gradient-to-r from-[#FFE400] to-[#BDFF00] hover:from-[#BDFF00] hover:to-[#FFE400] text-[#001B56] font-title font-extrabold rounded-2xl py-5 px-4 text-center text-base shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
                      onClick={() => setPlusOpen(false)}
                      style={{
                        animationDelay: `${index * 100}ms`
                      }}
                    >
                      {/* Effet de brillance */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                      
                      {/* Icône et texte */}
                      <div className="relative z-10">
                        <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-[#001B56]/10 flex items-center justify-center group-hover:bg-[#001B56]/20 transition-colors">
                          <MoreHorizontal size={16} className="text-[#001B56]" />
                        </div>
                        <span className="block leading-tight">{link.label}</span>
                      </div>
                    </NavLink>
                  ))}
                </div>

                {/* Section réseaux sociaux */}
                <div className="border-t border-[#FFE400]/20 pt-6">
                  <div className="text-center mb-4">
                    <span className="text-[#FFE400] text-xs font-accent font-semibold uppercase tracking-wider">
                      Suivez-nous
                    </span>
                  </div>
                  
                  <div className="flex justify-center gap-3">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group w-12 h-12 rounded-full bg-[#FFE400]/10 hover:bg-[#FFE400] flex items-center justify-center text-[#FFE400] hover:text-[#001B56] transition-all duration-300 hover:scale-110 transform"
                        style={{
                          animationDelay: `${(extraLinks.length + index) * 100}ms`
                        }}
                      >
                        <span className="text-lg group-hover:scale-110 transition-transform duration-200">
                          {social.icon}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Message d'encouragement */}
                <div className="text-center mt-6 px-4">
                  <p className="text-[#FFE400]/70 text-sm font-accent italic">
                    "Ensemble, supportons notre équipe !"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
